/**
 * Repositório: todas as leituras e escritas passam pela API do Quadro (POST /rpc).
 * As regras de acesso são garantidas no banco (RLS); aqui só há transporte e mapeamento.
 */
import {
  type AgendaEvent, type CalendarEvent, type Category, type MapData, type Note, type Project, type Routine, type Subtask, type Task,
  type Theme, type User, type WorkspaceSettings, OUTLOOK_CALENDAR, fmtMoneyShort, mapDataSchema, normalizeMapData,
} from '@quadro/domain';
import type { ProcessGraph } from '../../../api/src/processes/schema';
import { apiGet, apiPost, fetchSession, rpc } from '../lib/rpc';
import {
  routineColumns, taskColumns, toCategory, toEvent, toHistory, toNote, toProject, toRoutine, toSubtask, toTask, toTheme, toUser,
} from './mappers';
import type {
  CategoryRow, EventRow, NoteRow, OrgMapImportRow, ProfileRow, ProjectRow, RoutineRow, SubcategoryRow, SubtaskRow, TaskHistoryRow, TaskRow, ThemeRow,
} from './rows';

/* ---------- Pessoas ---------- */
/** Quem está logado (null = sem sessão). */
export const fetchMe = async () => { const r = await fetchSession<ProfileRow>(); return r ? toUser(r) : null; };
export const fetchPeople = async (): Promise<User[]> => (await rpc<ProfileRow[]>('people.list')).map(toUser);
export async function updateProfile(id: string, patch: Partial<Pick<User, 'name' | 'color' | 'role' | 'active'>>) {
  await rpc('people.update', { id, patch });
}

/* ---------- Configuração da área ---------- */
export async function fetchSettings(): Promise<WorkspaceSettings> {
  const r = await rpc<{ wip_limit: number; projects: ProjectRow[]; themes: ThemeRow[]; categories: CategoryRow[]; subcategories: SubcategoryRow[] }>('settings.get');
  return {
    wipLimit: r.wip_limit,
    projects: r.projects.map(toProject),
    themes: r.themes.map(toTheme),
    categories: r.categories.map(c => toCategory(c, r.subcategories)),
  };
}
export const saveWipLimit = (wipLimit: number) => rpc('settings.wip', { wipLimit });

/** Substitui projetos, temas e taxonomia pelo estado editado (ordem = posição). */
export async function saveTaxonomy(_before: WorkspaceSettings, next: Pick<WorkspaceSettings, 'projects' | 'themes' | 'categories'>) {
  await rpc('settings.taxonomy', {
    projects: next.projects.map(({ id, name, color }: Project) => ({ id, name, color })),
    themes: next.themes.map(({ id, name, color }: Theme) => ({ id, name, color })),
    categories: next.categories.map(({ id, name, subcategories }: Category) => ({ id, name, subcategories })),
  });
}

/* ---------- Tarefas ---------- */
export async function fetchTasks(): Promise<Task[]> {
  const r = await rpc<{ tasks: TaskRow[]; subtasks: SubtaskRow[] }>('tasks.list');
  const byTask = new Map<string, Subtask[]>();
  r.subtasks.forEach(s => byTask.set(s.task_id, [...(byTask.get(s.task_id) ?? []), toSubtask(s)]));
  return r.tasks.map(t => toTask(t, byTask.get(t.id) ?? []));
}
export const fetchTaskHistory = async (id: string) => (await rpc<TaskHistoryRow[]>('tasks.history', { id })).map(toHistory);

const subtaskRows = (subs: Subtask[]) => subs.map(s => ({ id: s.id, text: s.text, done: s.done, assignee_id: s.assigneeId, due: s.due }));

export async function createTask(t: Partial<Task> & { title: string }): Promise<string> {
  const r = await rpc<{ id: string }>('tasks.create', { row: taskColumns(t), subtasks: subtaskRows(t.subtasks ?? []) });
  return r.id;
}
/** Atualiza campos; se `subtasks` vier, substitui a lista. `log` vira uma entrada do histórico. Tudo numa transação. */
export async function updateTask(id: string, patch: Partial<Task>, log?: string) {
  const { subtasks, ...rest } = patch;
  await rpc('tasks.update', { id, patch: taskColumns(rest), subtasks: subtasks ? subtaskRows(subtasks) : undefined, log: log?.slice(0, 500) });
}
export const deleteTask = (id: string) => rpc('tasks.delete', { id });

/* ---------- Rotinas ---------- */
export const fetchRoutines = async (): Promise<Routine[]> => (await rpc<RoutineRow[]>('routines.list')).map(toRoutine);
export type RoutineDraft = Omit<Routine, 'id' | 'generatedThrough' | 'createdById' | 'createdAt'>;
export const saveRoutine = (id: string | null, r: RoutineDraft) => rpc('routines.save', { id, row: routineColumns(r) });
export const deleteRoutine = (id: string) => rpc('routines.delete', { id });
export const fetchHolidays = (from: string, to: string) => rpc<string[]>('holidays', { from, to });

/* ---------- Agenda ---------- */
export const fetchEvents = async (): Promise<CalendarEvent[]> => (await rpc<EventRow[]>('events.list')).map(toEvent);
export type EventDraft = Omit<CalendarEvent, 'id' | 'createdById'>;
export const saveEvent = (id: string | null, e: EventDraft) =>
  rpc('events.save', { id, row: { title: e.title, location: e.location, date: e.date, start_time: e.start, end_time: e.end, calendar: e.calendar } });
export const deleteEvent = (id: string) => rpc('events.delete', { id });

/* ---------- Notas ---------- */
export const fetchNotes = async (): Promise<Note[]> => (await rpc<NoteRow[]>('notes.list')).map(toNote);
export const createNote = async (pinned: boolean, id?: string): Promise<Note> => toNote(await rpc<NoteRow>('notes.create', { pinned, id }));
export const updateNote = (id: string, patch: Partial<Pick<Note, 'title' | 'body' | 'pinned'>>) => rpc('notes.update', { id, patch });
export const deleteNote = (id: string) => rpc('notes.delete', { id });

/* ---------- Mapa societário ---------- */
export interface MapSource {
  kind: 'live' | 'file';
  data: MapData;
  fileName?: string;
  importedAt?: string;
  importedById?: string | null;
}
export async function fetchLiveMap(): Promise<MapSource> {
  const parsed = mapDataSchema.parse(await rpc('map.live')) as MapData;
  return { kind: 'live', data: normalizeMapData(parsed, fmtMoneyShort) };
}
export async function fetchImportedMap(): Promise<MapSource | null> {
  const row = await rpc<OrgMapImportRow | null>('map.file');
  if (!row) return null;
  return { kind: 'file', data: normalizeMapData(row.data as unknown as MapData, fmtMoneyShort), fileName: row.file_name, importedAt: row.imported_at, importedById: row.imported_by };
}
export const importMap = (fileName: string, data: MapData) => rpc('map.import', { fileName, data });
export const fetchOfficialExposure = () =>
  rpc<{ entity_id: string; as_of: string; instrument_type: string; weight: number }[]>('map.exposure');

/* ---------- Agenda do Outlook (só da própria pessoa) ---------- */
interface OutlookRow { id: string; title: string; location: string; date: string; start: string; end: string; allDay: boolean; free: boolean; url: string | null }
export interface OutlookAgenda { connected: boolean; events: AgendaEvent[] }
export async function fetchOutlook(from: string, to: string): Promise<OutlookAgenda> {
  const r = await apiGet<{ connected: boolean; events: OutlookRow[] }>(`/outlook/events?from=${from}&to=${to}`);
  return {
    connected: r.connected,
    events: r.events.map(e => ({ ...e, calendar: OUTLOOK_CALENDAR.id, createdById: null, external: true })),
  };
}

/* ---------- Tokens pessoais (acesso pelo Claude via MCP) ---------- */
export interface ApiToken { id: string; name: string; prefix: string; readOnly: boolean; createdAt: string; lastUsedAt: string | null }
interface ApiTokenRow { id: string; name: string; prefix: string; read_only: boolean; created_at: string; last_used_at: string | null }
export const fetchTokens = async (): Promise<ApiToken[]> => (await rpc<ApiTokenRow[]>('tokens.list')).map(t => ({
  id: t.id, name: t.name, prefix: t.prefix, readOnly: t.read_only, createdAt: t.created_at, lastUsedAt: t.last_used_at,
}));
/** O token completo volta só aqui, uma vez. */
export const createToken = (name: string, readOnly: boolean) => rpc<{ id: string; token: string }>('tokens.create', { name, readOnly });
export const revokeToken = (id: string) => rpc('tokens.revoke', { id });

/* ---------- Automações das rotinas ---------- */
export type AutomationKind = 'email' | 'fund_report';
export interface AutomationRun { id: string; occurrence: string; status: 'sending' | 'sent' | 'failed' | 'test'; detail: string | null; recipients: string[]; at: string }
export interface AutomationConfig { to: string[]; cc: string[]; subject: string; body?: string; intro?: string }
export interface Automation { id: string; routineId: string; kind: AutomationKind; config: AutomationConfig; runAt: string; active: boolean; senderId: string | null; runs: AutomationRun[] }
interface AutomationRow { id: string; routine_id: string; kind: AutomationKind; config: AutomationConfig; run_at: string; active: boolean; sender_id: string | null; runs: AutomationRun[] }
export const fetchAutomations = async (routineId: string): Promise<Automation[]> => (await rpc<AutomationRow[]>('automations.list', { routineId })).map(a => ({
  id: a.id, routineId: a.routine_id, kind: a.kind, config: a.config, runAt: a.run_at, active: a.active, senderId: a.sender_id, runs: a.runs,
}));
export const saveAutomation = (a: { id: string | null; routineId: string; kind: AutomationKind; config: AutomationConfig; runAt: string; active: boolean }) =>
  rpc<{ id: string }>('automations.save', a);
export const deleteAutomation = (id: string) => rpc('automations.delete', { id });
export const previewAutomation = (d: { routineId: string; kind: AutomationKind; config: AutomationConfig }) =>
  apiPost<{ subject: string; html: string; to: string[]; cc: string[] }>('/automations/preview', d);
export const testAutomation = (id: string) => apiPost<{ sentTo: string }>(`/automations/${id}/test`);
/* ---------- Modelos de tarefa ---------- */
export interface TemplateSubtask { text: string; offsetDays: number | null }
export interface TaskTemplate {
  id: string; name: string; title: string; description: string; projectId: string | null; priority: number;
  themeIds: string[]; categoryId: string | null; subcategoryId: string | null; entityIds: string[];
  links: { url: string; title: string }[]; subtasks: TemplateSubtask[]; dueInDays: number | null;
}
type TemplateRow = { id: string; name: string; title: string; description: string; project_id: string | null; priority: number; theme_ids: string[]; category_id: string | null; subcategory_id: string | null; entity_ids: string[]; links: TaskTemplate['links']; subtasks: TemplateSubtask[]; due_in_days: number | null };
export const fetchTemplates = async (): Promise<TaskTemplate[]> => (await rpc<TemplateRow[]>('templates.list')).map(r => ({
  id: r.id, name: r.name, title: r.title, description: r.description, projectId: r.project_id, priority: r.priority, themeIds: r.theme_ids,
  categoryId: r.category_id, subcategoryId: r.subcategory_id, entityIds: r.entity_ids, links: r.links,
  subtasks: r.subtasks.map(s => ({ text: s.text, offsetDays: s.offsetDays ?? null })), dueInDays: r.due_in_days,
}));
export const saveTemplate = (id: string | null, t: Omit<TaskTemplate, 'id'>) => rpc<{ id: string }>('templates.save', {
  id, row: {
    name: t.name, title: t.title, description: t.description, project_id: t.projectId, priority: t.priority, theme_ids: t.themeIds,
    category_id: t.categoryId, subcategory_id: t.subcategoryId, entity_ids: t.entityIds, links: t.links, subtasks: t.subtasks, due_in_days: t.dueInDays,
  },
});
export const deleteTemplate = (id: string) => rpc('templates.delete', { id });
export const applyTemplate = (id: string, opts: { status?: string; assigneeId?: string | null } = {}) => rpc<{ id: string }>('templates.use', { id, ...opts });

/* ---------- Avisos no Teams (só admin) ---------- */
export type TeamsKind = 'assigned' | 'mentioned' | 'comment';
export interface TeamsConfig {
  urlHint: string; kinds: TeamsKind[]; mentionPeople: boolean; digest: boolean; digestAt: string;
  lastOkAt: string | null; lastError: string | null; lastErrorAt: string | null;
  lastDigest: { day: string; status: string; detail: string | null } | null;
}
export const fetchTeams = () => apiGet<TeamsConfig | null>('/teams');
export const saveTeams = (c: { url?: string; kinds: TeamsKind[]; mentionPeople: boolean; digest: boolean; digestAt: string }) => apiPost<null>('/teams', c);
export const clearTeams = () => apiPost<null>('/teams/clear');
export const testTeams = (url?: string) => apiPost<{ ok: boolean }>('/teams/test', { url });
export const fetchFeatures = () => apiGet<{ calendar: boolean; mail: boolean }>('/features');

/* ---------- Processos e fluxo de dados ---------- */
export type { NodeKind, EdgeKind, ProcessNode, ProcessEdge, ProcessGraph } from '../../../api/src/processes/schema';
export interface ProcessSummary { id: string; name: string; description: string; projectId: string | null; version: number; updatedAt: string; updatedBy: string | null; nodeCount: number; routineCount: number }
export interface Process { id: string; name: string; description: string; projectId: string | null; graph: ProcessGraph; version: number; updatedAt: string; updatedBy: string | null }
interface ProcessRow { id: string; name: string; description: string; project_id: string | null; graph: ProcessGraph; version: number; updated_at: string; updated_by: string | null; node_count?: number; routine_count?: number }
export const fetchProcesses = async (): Promise<ProcessSummary[]> => (await rpc<ProcessRow[]>('processes.list')).map(p => ({
  id: p.id, name: p.name, description: p.description, projectId: p.project_id, version: p.version, updatedAt: p.updated_at, updatedBy: p.updated_by,
  nodeCount: p.node_count ?? 0, routineCount: p.routine_count ?? 0,
}));
export const fetchProcess = async (id: string): Promise<Process> => {
  const p = await rpc<ProcessRow>('processes.get', { id });
  return { id: p.id, name: p.name, description: p.description, projectId: p.project_id, graph: p.graph, version: p.version, updatedAt: p.updated_at, updatedBy: p.updated_by };
};
export const saveProcess = (p: { id: string | null; name: string; description: string; projectId: string | null; graph: ProcessGraph; version?: number }) =>
  rpc<{ id: string; version: number }>('processes.save', p);
export const deleteProcess = (id: string) => rpc('processes.delete', { id });
export const fetchProcessesByRoutine = (routineId: string) => rpc<{ id: string; name: string }[]>('processes.byRoutine', { routineId });

/* ---------- Comentários e avisos ---------- */
export interface Comment { id: string; taskId: string; authorId: string; body: string; mentions: string[]; createdAt: string; editedAt: string | null }
interface CommentRow { id: string; task_id: string; author_id: string; body: string; mentions: string[]; created_at: string; edited_at: string | null }
export const fetchComments = async (taskId: string): Promise<Comment[]> => (await rpc<CommentRow[]>('comments.list', { taskId })).map(c => ({
  id: c.id, taskId: c.task_id, authorId: c.author_id, body: c.body, mentions: c.mentions, createdAt: c.created_at, editedAt: c.edited_at,
}));
export const addComment = (taskId: string, body: string, mentions: string[]) => rpc<{ id: string }>('comments.add', { taskId, body, mentions });
export const updateComment = (id: string, body: string, mentions: string[]) => rpc('comments.update', { id, body, mentions });
export const deleteComment = (id: string) => rpc('comments.delete', { id });

export interface Notice { id: number; kind: 'assigned' | 'mentioned' | 'comment'; taskId: string | null; actorId: string | null; text: string; createdAt: string; readAt: string | null; taskTitle: string | null }
interface NoticeRow { id: number; kind: Notice['kind']; task_id: string | null; actor_id: string | null; text: string; created_at: string; read_at: string | null; task_title: string | null }
export const fetchNotices = async (): Promise<Notice[]> => (await rpc<NoticeRow[]>('notifications.list')).map(n => ({
  id: Number(n.id), kind: n.kind, taskId: n.task_id, actorId: n.actor_id, text: n.text, createdAt: n.created_at, readAt: n.read_at, taskTitle: n.task_title,
}));
export const markNoticesRead = (ids?: number[]) => rpc<{ marked: number }>('notifications.read', ids ? { ids } : {});
